import { useMember } from '@features/members/hooks/useMembers';
import { styled } from '@mui/joy';
import { userQueries } from '@entities/User/queries';
import FetchError from '@services/api/utils/FetchError';
import { format2CodeTo3Code } from '@root/src/utils/formatters/countries';
import { formatFullName } from '@root/src/utils/formatters/strings';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

export const MemberSidebar = ({ id }: { id: string }) => {
  const { t } = useTranslation();
  const { data: { name = '-' } = {}, isLoading, isError, error } = useMember(id);
  const isDeletedMember = isError && error instanceof FetchError && error.response?.status === 404;
  const displayText = isLoading ? '...' : isDeletedMember ? t('api.error.memberDeleted') : isError ? '-' : name;
  return <OrgSidebar>{displayText}</OrgSidebar>;
};

const stopPropagation = (e: React.MouseEvent) => {
  e.stopPropagation();
};

// Link cannot be styled with Joy styled()
export const MemberLink = ({ id }: { id: string }) => {
  const { t } = useTranslation();
  const { data: { name } = {}, isLoading, isError, error } = useMember(id);
  const isDeletedMember = isError && error instanceof FetchError && error.response?.status === 404;

  if (isLoading) {
    return '...';
  }

  if (isDeletedMember) {
    return <Span>{t('api.error.memberDeleted')}</Span>;
  }

  if (isError || !name) {
    return <Span>-</Span>;
  }

  return (
    <Link href={`/directory/member/${id}`} passHref>
      <LinkText onClick={stopPropagation}>{name}</LinkText>
    </Link>
  );
};

export const MemberLinkBlue = ({ id }: { id: string }) => {
  const { t } = useTranslation();
  const { data: { name } = {}, isLoading, isError, error } = useMember(id);
  const isDeletedMember = isError && error instanceof FetchError && error.response?.status === 404;

  if (isLoading) {
    return '...';
  }

  if (isDeletedMember) {
    return <Span>{t('api.error.memberDeleted')}</Span>;
  }

  if (isError || !name) {
    return <Span>-</Span>;
  }

  return (
    <Link href={`/directory/member/${id}`} passHref>
      <BlueLinkText onClick={stopPropagation}>{name}</BlueLinkText>
    </Link>
  );
};

export const ContactLink = ({ id, color }: { id: string; color?: string }) => {
  const { data: { firstName, lastName, member } = {}, isLoading } = useQuery({
    ...userQueries.detail(id),
    // Dead pointOfContact references (deleted users, historical ETL rows) 404
    // here and stay cached as errored queries; a window-focus event would
    // refire all of them at once (same storm class as the member-byline 404s
    // that OOM-killed UAT). Don't refire stable failures automatically.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (isLoading) {
    return '...';
  }

  if (!firstName && !lastName) {
    return <Span>-</Span>;
  }

  const organizationName = member?.name || '';
  const userName = formatFullName(firstName, lastName);
  const displayText = organizationName ? `${userName} (${organizationName})` : userName;

  return (
    <Link href={`/directory/point-of-contact/${id}`} passHref>
      <LinkText onClick={stopPropagation} style={color ? { color } : {}}>
        {displayText}
      </LinkText>
    </Link>
  );
};

export const OperatorLink = ({ id }: { id: string }) => {
  const { t } = useTranslation();
  const { data: { name, registrationAddress: { country } = {} } = {}, isLoading, isError, error } = useMember(id);
  const isDeletedMember = isError && error instanceof FetchError && error.response?.status === 404;

  if (isLoading) {
    return '...';
  }

  if (isDeletedMember) {
    return <Span>{t('api.error.memberDeleted')}</Span>;
  }

  if (isError || !name) {
    return <Span>-</Span>;
  }

  const formattedCountry = country ? format2CodeTo3Code(country) : '-';

  return (
    <span>
      <Link href={`/directory/member/${id}`} passHref>
        <LinkText onClick={stopPropagation}>{name}</LinkText>
      </Link>
      &nbsp;
      <Span>{formattedCountry}</Span>
    </span>
  );
};

const LinkText = styled('span')(({ theme: { palette, typography } }) => ({
  ...typography['body-small'],
  color: palette.grey5,
  textDecoration: 'underline',
}));

const BlueLinkText = styled('span')(({ theme: { palette, typography } }) => ({
  ...typography['body-small'],
  color: palette.bluePrimary,
  textDecoration: 'underline',
}));

const Span = styled('span')(({ theme: { palette, typography } }) => ({
  ...typography['body-small'],
  color: palette.grey5,
}));

const OrgSidebar = styled('span')(({ theme: { typography, palette } }) => ({
  ...typography['body-small'],
  color: palette.grey4,
  display: 'block',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
}));
